import { Injectable, OnDestroy, inject, signal, computed } from '@angular/core';
import { LRU } from '../shared/lru-cache';
import { WorkerPoolService, getWorkerPool } from './worker-pool.service';

/**
 * Service to manage global viewer configuration state within the Angular lifecycle.
 *
 * This service is the single source of truth for:
 * - Point cloud rendering configuration (pointBudget, maxNodesLoading, etc.)
 * - Token management (SAS token for blob storage, access token for API auth)
 * - Worker pool and LRU cache lifecycle
 *
 * Non-DI modules access this state via the exported functions in this file.
 */
@Injectable({ providedIn: 'root' })
export class ConfigService implements OnDestroy {
  readonly pointBudget = signal(1_000_000);
  readonly pointLoadLimit = computed(() => this.pointBudget() * 2);
  readonly maxNodesLoading = signal(4);
  readonly numNodesLoading = signal(0);
  readonly framenumber = signal(0);
  readonly sasToken = signal<string | null>(null);
  readonly accessToken = signal<string | null>(null);

  readonly workerPool = inject(WorkerPoolService);
  readonly lru = new LRU();

  // Transform tracking per point cloud
  private _pointcloudTransformVersions = new WeakMap<any, { number: number; transform: any }>();

  constructor() {
    _setServiceInstance(this);
  }

  ngOnDestroy(): void {
    // Clean up LRU cache
    this.lru.freeMemory(this.pointLoadLimit());

    // Clear tokens
    this.updateSasToken(null);
    this.updateAccessToken(null);

    _setServiceInstance(null);
  }

  incrementNodesLoading(): void {
    this.numNodesLoading.update(n => n + 1);
  }

  decrementNodesLoading(): void {
    this.numNodesLoading.update(n => Math.max(0, n - 1));
  }

  canLoadMoreNodes(): boolean {
    return this.numNodesLoading() < this.maxNodesLoading();
  }

  incrementFramenumber(): void {
    this.framenumber.update(n => n + 1);
  }

  updateSasToken(token: string | null): void {
    this.sasToken.set(token);
  }

  updateAccessToken(token: string | null): void {
    this.accessToken.set(token);
  }

  getTransformVersion(pointcloud: any): { number: number; transform: any } | undefined {
    return this._pointcloudTransformVersions.get(pointcloud);
  }

  setTransformVersion(pointcloud: any, version: { number: number; transform: any }): void {
    this._pointcloudTransformVersions.set(pointcloud, version);
  }

  hasTransformVersion(pointcloud: any): boolean {
    return this._pointcloudTransformVersions.has(pointcloud);
  }

  reset(): void {
    this.pointBudget.set(1_000_000);
    this.maxNodesLoading.set(4);
    this.numNodesLoading.set(0);
    this.framenumber.set(0);
    this._pointcloudTransformVersions = new WeakMap();
    this.updateSasToken(null);
    this.updateAccessToken(null);
  }
}

// =============================================================================
// Module-level accessors for non-DI code
// =============================================================================

let _instance: ConfigService | null = null;

/** Called internally by the service constructor/destructor */
function _setServiceInstance(instance: ConfigService | null): void {
  _instance = instance;
}

export function getGlobalConfig(): any {
  if (_instance) {
    return {
      pointBudget: _instance.pointBudget(),
      maxNodesLoading: _instance.maxNodesLoading(),
      numNodesLoading: _instance.numNodesLoading(),
      transformVersions: new Map(),
      framenumber: _instance.framenumber(),
    };
  }
  return {
    pointBudget: 1_000_000,
    maxNodesLoading: 4,
    numNodesLoading: 0,
    transformVersions: new Map(),
    framenumber: 0,
  };
}

export function setGlobalConfig(config: any): void {
  if (_instance && config) {
    if (config.pointBudget != null) _instance.pointBudget.set(config.pointBudget);
    if (config.maxNodesLoading != null) _instance.maxNodesLoading.set(config.maxNodesLoading);
    if (config.numNodesLoading != null) _instance.numNodesLoading.set(config.numNodesLoading);
    if (config.framenumber != null) _instance.framenumber.set(config.framenumber);
  }
}

export function incrementNodesLoading(): number {
  if (_instance) {
    _instance.incrementNodesLoading();
    return _instance.numNodesLoading();
  }
  return 0;
}

export function decrementNodesLoading(): number {
  if (_instance) {
    _instance.decrementNodesLoading();
    return _instance.numNodesLoading();
  }
  return 0;
}

export function incrementFrameNumber(): number {
  if (_instance) {
    _instance.incrementFramenumber();
    return _instance.framenumber();
  }
  return 0;
}

// ─── Token accessors ──────────────────────────────────────────────────────────

export function getSasToken(): string | null {
  return _instance ? _instance.sasToken() : null;
}

export function getAccessToken(): string | null {
  return _instance ? _instance.accessToken() : null;
}

// ─── LRU accessor ─────────────────────────────────────────────────────────────

export function getLru(): LRU {
  if (!_instance) {
    throw new Error('ConfigService not yet initialized');
  }
  return _instance.lru;
}

// ─── Config value accessors ───────────────────────────────────────────────────

export function getPointBudget(): number {
  return _instance ? _instance.pointBudget() : 1_000_000;
}

export function getPointLoadLimit(): number {
  return _instance ? _instance.pointLoadLimit() : 2_000_000;
}

export function getNumNodesLoading(): number {
  return _instance ? _instance.numNodesLoading() : 0;
}

export function getMaxNodesLoading(): number {
  return _instance ? _instance.maxNodesLoading() : 4;
}

// ─── Misc ─────────────────────────────────────────────────────────────────────

/** Debug object for runtime diagnostics */
export const debug: Record<string, any> = {};

export { getWorkerPool };

export const workerPool = {
  getWorker(key: string, factory: () => Worker): Worker {
    return getWorkerPool().getWorker(key, factory);
  },
  returnWorker(key: string, worker: Worker): void {
    return getWorkerPool().returnWorker(key, worker);
  },
};

// Path configuration
const origin = typeof window !== 'undefined' ? window.location.origin : '';
export const resourcePath = origin;
